import { useCallback, useState } from "react";

import { Trans } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { HealthConsentGate } from "@/components/consent/health-consent-sheet";
import { PageHeader } from "@/components/layout/page-header";
import {
  MedicalVaultPage,
  TREATMENT_SUBTABS,
  VAULT_TABS,
  VaultBodySkeleton,
  type TreatmentSubTab,
  type VaultTab,
} from "@/components/account/medical-vault/medical-vault-page";
import { prefetchVaultGateStatus } from "@/components/account/medical-vault/protected-content";
import { CONSENTS_QUERY_KEY, type ConsentsResponse } from "@/lib/hooks/use-consents";

interface MedicalVaultSearch {
  tab: VaultTab;
  subtab: TreatmentSubTab;
  member?: string;
}

export const Route = createFileRoute("/_authenticated/account/user/medical-vault")({
  validateSearch: (search: Record<string, unknown>): MedicalVaultSearch => {
    const rawTab = typeof search.tab === "string" ? search.tab : "";
    const rawSubtab = typeof search.subtab === "string" ? search.subtab : "";
    const rawMember = typeof search.member === "string" ? search.member : "";
    const tab = VAULT_TABS.includes(rawTab as VaultTab) ? (rawTab as VaultTab) : "passport";
    return {
      tab,
      // Treatments has no subtabs any more (one "coming soon" card); the param
      // is still accepted so existing links keep validating.
      subtab: TREATMENT_SUBTABS.includes(rawSubtab as TreatmentSubTab)
        ? (rawSubtab as TreatmentSubTab)
        : "active",
      // Only the Journal is per family member — every other tab is the
      // signed-in user's own record, so ?member never applies there.
      member: tab === "journal" ? rawMember || undefined : undefined,
    };
  },
  // P32: read the consent and the PIN-gate status in parallel while the route
  // chunk loads, instead of one after the other behind blank screens. Both
  // are status reads, never health data.
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery({
      queryKey: CONSENTS_QUERY_KEY,
      queryFn: () => apiFetch.get<ConsentsResponse>("/api/account/consent"),
      staleTime: 60_000,
    });
    void prefetchVaultGateStatus(context.queryClient);
  },
  component: MedicalVaultRoute,
});

function MedicalVaultRoute() {
  const { tab: activeTab, subtab: activeSubTab, member } = Route.useSearch();
  const navigate = useNavigate();
  /** Header slot the gated Lock button is portalled into. */
  const [headerActionsEl, setHeaderActionsEl] = useState<HTMLDivElement | null>(null);

  const activeMemberId = member ?? null;

  const setActiveTab = useCallback(
    (next: VaultTab) => {
      navigate({
        to: "/account/user/medical-vault",
        // Switching tab clears subtab back to default to mirror legacy behavior;
        // the member selection belongs to the Journal and is dropped elsewhere.
        search: {
          tab: next,
          subtab: "active",
          member: next === "journal" ? member : undefined,
        } satisfies MedicalVaultSearch,
      });
    },
    [navigate, member],
  );

  const setActiveMember = useCallback(
    (next: string | null) => {
      navigate({
        to: "/account/user/medical-vault",
        search: {
          tab: activeTab,
          subtab: activeSubTab,
          member: next ?? undefined,
        } satisfies MedicalVaultSearch,
      });
    },
    [navigate, activeTab, activeSubTab],
  );

  return (
    <div>
      {/* Static header outside both gates (P32): it holds no health data, so
          it shows at once while consent and PIN status load. */}
      <PageHeader
        title={<Trans>Medical vault</Trans>}
        description={<Trans>Your encrypted personal health record.</Trans>}
        actions={<div ref={setHeaderActionsEl} className="empty:hidden" />}
      />
      {/* E-1: vault + journal hold special-category data — explicit Art. 9
          consent is captured at this first touchpoint before anything loads. */}
      <HealthConsentGate fallback={<VaultBodySkeleton />}>
        <MedicalVaultPage
          activeTab={activeTab}
          activeMemberId={activeMemberId}
          setActiveTab={setActiveTab}
          setActiveMember={setActiveMember}
          headerActionsSlot={headerActionsEl}
        />
      </HealthConsentGate>
    </div>
  );
}
